import React, { useEffect, useState } from "react";
import { Card, Row, Col, Table, Button, Badge, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./admin.css";

import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/news`;
const IMAGE_URL = `${process.env.REACT_APP_API_URL}/uploads/`;
const newsPlaceholder = "https://placehold.co/400x250?text=No+Image";
const adPlaceholder = "https://placehold.co/900x500?text=No+Ad";

const Dashboard = () => {
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalNews: 0,
        published: 0,
        draft: 0,
        categories: 0,
        videos: 0,
        ads: 0,
    });

    const [recentNews, setRecentNews] = useState([]);
    const [chartData, setChartData] = useState({});
    const [refreshInterval, setRefreshInterval] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);

    useEffect(() => {
        fetchDashboardData();
        // Auto-refresh dashboard every 5 seconds for real-time updates
        const interval = setInterval(fetchDashboardData, 5000);
        setRefreshInterval(interval);
        return () => clearInterval(interval);
    }, []);

    // ✅ RESOLVE IMAGE URL (Cloudinary + Local + Fallback)
    const resolveUrl = (path) => {
        if (!path) return null;
        if (typeof path !== "string") return null;
        if (path.startsWith("http")) return path;
        return `${IMAGE_URL}${path}`;
    };

    // ✅ GET NEWS IMAGE
    const getImage = (item) => {
        return (
            resolveUrl(item?.imagePath) ||
            resolveUrl(item?.image) ||
            newsPlaceholder
        );
    };

    // ✅ GET AD IMAGE
    const getAdImage = (item) => {
        if (!item?.adImage && !item?.image) return adPlaceholder;
        return (
            resolveUrl(item?.adImage) ||
            resolveUrl(item?.image) ||
            adPlaceholder
        );
    };

    // ✅ EXTRACT YOUTUBE ID
    const extractYouTubeId = (input) => {
        if (!input) return null;
        const str = String(input).trim();
        if (!str) return null;
        const iframeSrcMatch = str.match(/src=["']([^"']+)["']/i);
        const value = iframeSrcMatch ? iframeSrcMatch[1] : str;
        if (/^[a-zA-Z0-9_-]{11}$/.test(value)) return value;
        const fixed = /^https?:\/\//i.test(value) ? value : `https://${value}`;
        const patterns = [
            /youtu\.be\/([a-zA-Z0-9_-]{11})/,
            /[?&]v=([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
        ];
        for (const p of patterns) {
            const match = fixed.match(p);
            if (match) return match[1];
        }
        try {
            const url = new URL(fixed);
            return url.searchParams.get("v") || null;
        } catch {
            return null;
        }
    };

    // ✅ GET VIDEO THUMBNAIL
    const getVideoThumbnail = (videoUrl) => {
        if (!videoUrl) return null;
        const id = extractYouTubeId(videoUrl);
        if (!id) return null;
        return `https://img.youtube.com/vi/${id}/default.jpg`;
    };

    // ✅ HANDLE IMAGE ERROR
    const handleImgError = (e, kind = "news") => {
        e.target.onerror = null;
        e.target.src = kind === "ad" ? adPlaceholder : newsPlaceholder;
    };

    // ================= FETCH =================
    const fetchDashboardData = async () => {
        try {
            // 🔄 Cache-busting parameter to ensure fresh data
            const timestamp = new Date().getTime();
            const res = await fetch(`${API_URL}?t=${timestamp}`, {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            });
            const result = await res.json();
            const news = result?.data || [];

            const totalNews = news.length;
            const published = news.filter((n) => n.status === "Published").length;
            const draft = news.filter((n) => n.status === "Draft").length;

            // 🎥 VIDEO COUNT
            const videos = news.filter((n) => n.videoUrl).length;

            // 📢 ADS COUNT
            const ads = news.filter((n) => n.adImage).length;

            // CATEGORY MAP
            const categoryMap = {};
            news.forEach((item) => {
                const cat = item.category || "Other";
                categoryMap[cat] = (categoryMap[cat] || 0) + 1;
            });

            // CHART
            setChartData({
                labels: Object.keys(categoryMap),
                datasets: [
                    {
                        label: "News Count",
                        data: Object.values(categoryMap),
                        backgroundColor: [
                            "#dc3545",
                            "#28a745",
                            "#0dcaf0",
                            "#ffc107",
                            "#6c757d",
                            "#6610f2",
                        ],
                        borderRadius: 6,
                    },
                ],
            });

            // 📌 Show only PUBLISHED articles in recent (sorted by date)
            const recent = [...news]
                .filter((n) => n.status === "Published")
                .sort((a, b) => new Date(b.createdAt || b.publishedAt) - new Date(a.createdAt || a.publishedAt))
                .slice(0, 5);

            setStats({
                totalNews,
                published,
                draft,
                categories: Object.keys(categoryMap).length,
                videos,
                ads,
            });

            setRecentNews(recent);
        } catch (err) {
            console.error("Dashboard Error:", err);
        }
    };

    return (
        <>
            <h3 className="mb-4 fw-bold">📊 Admin Dashboard</h3>

            {/* ================= STATS ================= */}
            <Row className="g-4 mb-4">
                <StatCard title="Total News" value={stats.totalNews} color="danger" icon="📰" />
                <StatCard title="Published" value={stats.published} color="success" icon="✅" />
                <StatCard title="Draft" value={stats.draft} color="secondary" icon="📝" />
                <StatCard title="Categories" value={stats.categories} color="primary" icon="📂" />
                <StatCard title="Videos" value={stats.videos} color="warning" icon="🎥" />
                <StatCard title="Ads" value={stats.ads} color="info" icon="📢" />
            </Row>

            {/* ================= CHART + RECENT ================= */}
            <Row className="g-4 mb-4">

                {/* CHART */}
                <Col md={6}>
                    <Card className="admin-card p-4 shadow-sm">
                        <h5 className="fw-bold mb-3">📊 Category Distribution</h5>

                        {chartData?.labels && (
                            <Bar
                                data={chartData}
                                options={{
                                    responsive: true,
                                    plugins: { legend: { display: false } },
                                }}
                            />
                        )}
                    </Card>
                </Col>

                {/* RECENT NEWS */}
                <Col md={6}>
                    <Card className="admin-card p-4 shadow-sm">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold m-0">📰 Recent Articles</h5>
                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={fetchDashboardData}
                                title="Refresh"
                            >
                                🔄
                            </Button>
                        </div>

                        <Table hover responsive style={{ fontSize: "13px" }}>
                            <thead>
                                <tr>
                                    <th style={{ width: "80px" }}>Image</th>
                                    <th style={{ width: "150px" }}>Title</th>
                                    <th style={{ width: "70px" }}>Video</th>
                                    <th style={{ width: "70px" }}>Ad</th>
                                    <th style={{ width: "70px" }}>Status</th>
                                </tr>
                            </thead>

                            <tbody>
                                {recentNews.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted py-3">
                                            No articles yet
                                        </td>
                                    </tr>
                                ) : (
                                    recentNews.map((item, i) => (
                                        <tr key={i} className="align-middle">
                                            {/* IMAGE */}
                                            <td>
                                                <div className="position-relative overflow-hidden" style={{
                                                    width: "70px",
                                                    height: "50px",
                                                    borderRadius: "4px",
                                                    backgroundColor: "#f0f0f0"
                                                }}>
                                                    <img
                                                        src={getImage(item)}
                                                        alt={item.title}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                        }}
                                                        onError={(e) => handleImgError(e, "news")}
                                                    />
                                                </div>
                                            </td>

                                            {/* TITLE + DESC */}
                                            <td>
                                                <strong className="d-block" style={{ fontSize: "12px", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {item.title}
                                                </strong>
                                                <small className="text-muted d-block" style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {item.shortDesc?.slice(0, 25)}...
                                                </small>
                                            </td>

                                            {/* VIDEO */}
                                            <td>
                                                {item.videoUrl ? (
                                                    <div
                                                        style={{
                                                            position: "relative",
                                                            width: "60px",
                                                            height: "40px",
                                                            cursor: "pointer",
                                                            borderRadius: "4px",
                                                            overflow: "hidden",
                                                            backgroundColor: "#000",
                                                        }}
                                                        onClick={() => {
                                                            setSelectedVideo(item.videoUrl);
                                                            setShowVideoModal(true);
                                                        }}
                                                    >
                                                        <img
                                                            src={getVideoThumbnail(item.videoUrl)}
                                                            alt="Video"
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                opacity: 0.7,
                                                            }}
                                                        />
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "50%",
                                                                left: "50%",
                                                                transform: "translate(-50%, -50%)",
                                                                fontSize: "20px",
                                                            }}
                                                        >
                                                            ▶
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>

                                            {/* AD IMAGE */}
                                            <td>
                                                {item.adImage ? (
                                                    <div style={{
                                                        width: "60px",
                                                        height: "40px",
                                                        borderRadius: "4px",
                                                        overflow: "hidden",
                                                        backgroundColor: "#f0f0f0"
                                                    }}>
                                                        <img
                                                            src={getAdImage(item)}
                                                            alt="Ad"
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                            }}
                                                            onError={(e) => handleImgError(e, "ad")}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>

                                            {/* STATUS */}
                                            <td>
                                                <Badge
                                                    bg={item.status === "Published" ? "success" : item.status === "Draft" ? "warning" : "secondary"}
                                                >
                                                    {item.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </Card>
                </Col>
            </Row>

            {/* ================= ACTIONS ================= */}
            <Row>
                <Col>
                    <Card className="admin-card p-4 shadow-sm">
                        <h5 className="fw-bold mb-3">⚡ Quick Actions</h5>

                        <div className="d-flex gap-3">
                            <Button variant="danger" onClick={() => navigate("/admin/add-news")}>
                                ➕ Add News
                            </Button>

                            <Button variant="dark" onClick={() => navigate("/admin/manage-news")}>
                                🛠 Manage News
                            </Button>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* ================= VIDEO MODAL ================= */}
            <Modal show={showVideoModal} onHide={() => setShowVideoModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>📹 Video Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedVideo && (
                        <div style={{ paddingBottom: "56.25%", position: "relative", height: 0, overflow: "hidden" }}>
                            <iframe
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                }}
                                src={`https://www.youtube-nocookie.com/embed/${extractYouTubeId(selectedVideo)}?rel=0&modestbranding=1&playsinline=1`}
                                title="Video"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

// ✅ STAT CARD
const StatCard = ({ title, value, color, icon }) => (
    <Col md={2}>
        <Card className="text-center p-3 shadow-sm">
            <div className={`bg-${color} text-white rounded p-2 mb-2`}>
                {icon}
            </div>
            <h6>{title}</h6>
            <h4 className={`text-${color}`}>{value}</h4>
        </Card>
    </Col>
);

export default Dashboard;